import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/common';
import CustomerForm from '../CustomerForm';
import { CreateCustomerRequest, UpdateCustomerRequest } from '@/types/customer';
import { useUIStore } from '@/store/uiStore';
import { toast } from '@/store/toastStore';
import { customerApi } from '@/api/endpoints/customers';

const EditCustomerModal: React.FC = () => {
  const { t } = useTranslation();
  const { modals, closeModal, editingCustomer } = useUIStore();
  const [loading, setLoading] = React.useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'detailed'>('basic');

  const handleSubmit = async (data: CreateCustomerRequest | UpdateCustomerRequest, driverId?: string) => {
    setLoading(true);
    try {
      await customerApi.updateCustomer({ ...(data as UpdateCustomerRequest), code: editingCustomer!.code });
      
      // Update driver assignment if changed
      if (driverId !== editingCustomer?.assignedDriverId) {
        try {
          await customerApi.assignDriver(editingCustomer!.code, driverId || null);
        } catch (driverError) {
          console.error('Error updating driver assignment:', driverError);
          toast.warning(t('common.warning'), 'Customer updated but driver assignment failed');
        }
      }
      
      toast.success(
        t('common.success'),
        t('customers.messages.updateSuccess')
      );
      closeModal('editCustomer');
      // Trigger refresh of customer list
      window.dispatchEvent(new CustomEvent('refreshCustomers'));
    } catch (error: any) {
      console.error('Error updating customer:', error);
      const errorMessage = error.message || t('common.error');
      toast.error(t('common.error'), errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndClose = async (data: CreateCustomerRequest | UpdateCustomerRequest, driverId?: string) => {
    setLoading(true);
    try {
      await customerApi.updateCustomer({ ...(data as UpdateCustomerRequest), code: editingCustomer!.code });
      
      // Update driver assignment if changed
      if (driverId !== editingCustomer?.assignedDriverId) {
        try {
          await customerApi.assignDriver(editingCustomer!.code, driverId || null);
        } catch (driverError) {
          console.error('Error updating driver assignment:', driverError);
          toast.warning(t('common.warning'), 'Customer updated but driver assignment failed');
        }
      }
      
      toast.success(
        t('common.success'),
        t('customers.messages.updateSuccess')
      );
      closeModal('editCustomer');
      // Trigger refresh of customer list
      window.dispatchEvent(new CustomEvent('refreshCustomers'));
    } catch (error: any) {
      console.error('Error updating customer:', error);
      const errorMessage = error.message || t('common.error');
      toast.error(t('common.error'), errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    closeModal('editCustomer');
  };



  return (
    <Modal
      isOpen={modals.editCustomer}
      onClose={handleCancel}
      title={t('customers.edit')}
      size="xl"
    >
      <div className="flex flex-col h-full">
        {/* Tab Navigation */}
        <div className="mobile-tab-scroll">
          <nav className="mobile-tab-list px-4 sm:px-6" aria-label="Tabs">
            <button
              type="button"
              onClick={() => setActiveTab('basic')}
              className={`mobile-tab-button border-b-2 ${
                activeTab === 'basic'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('customers.tabs.info')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('detailed')}
              className={`mobile-tab-button border-b-2 ${
                activeTab === 'detailed'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('customers.tabs.detailedInfo')}
            </button>

          </nav>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden">
          <CustomerForm
            customer={editingCustomer}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            onSaveAndClose={handleSaveAndClose}
            loading={loading}
            currentTab={activeTab}
            hideTabNavigation={true}
          />
        </div>
      </div>
    </Modal>
  );
};

export default EditCustomerModal;
